import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import AutoRenewalModal from '@/enterprise/components/AutoRenewalModal';
import useEnablePricing from '@/enterprise/components/OfferBuilder/useEnablePricing';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useTurnOffParticipation } from '@/enterprise/data/useTurnOffParticipation';
import { useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { useReporting } from '@/reporting';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import { useEditRateModalContext } from './EditRateModal/EditRateModalContext';
import { useEditScheduleModalContext } from './EditScheduleModal/EditScheduleModalContext';
import { getOfferBuilderNavigationState } from './OfferActivityCards';
import useParticipationAction from './useParticipationAction';

const DivisionTableActionsDropdown = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const [autoRenewalModalOpen, setAutoRenewalModalOpen] = useState(false);
  const enablePricing = useEnablePricing();
  const enableUnification = useEnableUnification();
  const enableAutomations = useEnableAutomations();
  const { getRestrictions } = useRestrictions();
  const { trackEnterpriseEvent } = useReporting();
  const { openModal: openOfferModal, openDeleteScheduleModal } = useOfferModalContext();
  const { openModal: openEditRateModal } = useEditRateModalContext();
  const { openModal: openEditScheduleModal } = useEditScheduleModalContext();
  const { turnOffParticipation, isLoading: turnOffParticipationIsLoading } = useTurnOffParticipation(takerMarket);
  const { executeParticipationAction } = useParticipationAction();
  const navigationState = getOfferBuilderNavigationState([takerMarket], { expandSelected: true });
  const { canEditOffers } = getRestrictions([takerMarket]);
  const { makerOrganizationUuid, marketId, takerDivisionId } = takerMarket;
  const hasPreferredOffer = takerMarket.offerConfig.offerType === 'PREFERRED_TERM';
  const isManuallyAwardedWithActiveOffer = takerMarket.offerConfig.isManuallyAwarded;
  // An express-accept offer can have all invoices removed from clear, making isManuallyAwarded false even though the offer is still active
  const canModifyOffer =
    canEditOffers &&
    !isManuallyAwardedWithActiveOffer &&
    !hasPreferredOffer &&
    !takerMarket.offerConfig.isActiveExpressAccept;
  const canShowModifyOfferAction = canModifyOffer && takerMarket.marketIsEnabled;
  const canShowQuickRateEdit =
    canModifyOffer && takerMarket.marketIsEnabled && takerMarket.marketPricingType === 'PRICE_DISCOVERY';
  const canShowQuickScheduleEdit = canModifyOffer && enableUnification;
  const canTurnOffParticipation = canModifyOffer && takerMarket.offerConfig.isEnabled;

  const handleBuildOffer = () => {
    trackEnterpriseEvent('market-actions-menu::build-offer::clicked', {
      makerOrganizationUuid,
      marketId,
      takerDivisionId,
    });
  };

  const handleRateEdit = () => {
    trackEnterpriseEvent('market-actions-menu::edit-rate::clicked', {
      makerOrganizationUuid,
      marketId,
      takerDivisionId,
    });

    if (enablePricing) {
      openEditRateModal(takerMarket);
    } else {
      openOfferModal({ takerMarket, initialAccordionValue: 'Rate', referrer: 'offer' });
    }
  };

  const handleScheduleEdit = () => {
    trackEnterpriseEvent('market-actions-menu::edit-schedule::clicked', {
      makerOrganizationUuid,
      marketId,
      takerDivisionId,
    });

    openEditScheduleModal(takerMarket);
  };

  const handleDeleteSchedule = () => {
    trackEnterpriseEvent('market-actions-menu::delete-schedule::clicked', {
      makerOrganizationUuid,
      marketId,
      takerDivisionId,
    });

    openDeleteScheduleModal(takerMarket);
  };

  const handleViewHistory = () => {
    trackEnterpriseEvent('market-actions-menu::view-history::clicked', {
      makerOrganizationUuid,
      marketId,
      takerDivisionId,
    });
  };

  const handleTurnOffParticipation = async () => {
    if (!canTurnOffParticipation) {
      return;
    }

    trackEnterpriseEvent('market-actions-menu::participation::clicked', { marketId, takerDivisionId });

    await executeParticipationAction(() => turnOffParticipation(), {
      messageId: `${takerMarket.makerOrganizationUuid}-${takerMarket.marketUuid}-${takerMarket.takerDivisionUuid}`,
      errorTitle: 'Enterprise Division Participation Off Error',
      errorLocation: 'OfferActivity -> DivisionTableActionsDropdown -> handleTurnOffParticipation',
      errorContext: {
        takerMarket: {
          makerOrganizationUuid: takerMarket.makerOrganizationUuid,
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
        },
      },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton icon={EllipsisVerticalIcon} name={t('core.divisionActions')} size="sm" variant="ancillary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
          {hasPreferredOffer ? (
            <DropdownMenuItem onClick={() => setAutoRenewalModalOpen(true)}>
              {t('offer.autoRenew.modifyRenewal')}
            </DropdownMenuItem>
          ) : (
            <>
              {takerMarket.offerConfig.isEnabled || takerMarket.offerSchedule ? (
                <DropdownMenuItem disabled={!canShowQuickRateEdit} onClick={handleRateEdit}>
                  {t('builder.editRate')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem asChild disabled={!canShowModifyOfferAction} onClick={handleBuildOffer}>
                  <Link state={navigationState} to={asEnterpriseLink('build-offer')}>
                    {t('core.buildOffer')}
                  </Link>
                </DropdownMenuItem>
              )}
              {enableAutomations && takerMarket.offerSchedule ? (
                <DropdownMenuItem disabled={!canShowQuickScheduleEdit} onClick={handleScheduleEdit}>
                  {t('editScheduleModal.editSchedule')}
                </DropdownMenuItem>
              ) : null}
              {takerMarket.offerSchedule ? (
                <DropdownMenuItem disabled={!canModifyOffer} onClick={handleDeleteSchedule}>
                  {t('automation.deleteSchedule')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={!canTurnOffParticipation || turnOffParticipationIsLoading}
                  onClick={handleTurnOffParticipation}
                >
                  {t('globalActions.turnParticipationOff')}
                </DropdownMenuItem>
              )}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link
              onClick={handleViewHistory}
              to={asEnterpriseLink(
                `customers/${makerOrganizationUuid}/markets/${marketId}/division/${takerDivisionId}/history`
              )}
            >
              {t('core.viewHistory')}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {hasPreferredOffer && (
        <AutoRenewalModal
          open={autoRenewalModalOpen}
          onClose={() => setAutoRenewalModalOpen(false)}
          takerMarket={takerMarket}
        />
      )}
    </>
  );
};

export default DivisionTableActionsDropdown;
